(function () {
  if (window.__edPdpFreeGiftCartSyncLoaded) return;
  window.__edPdpFreeGiftCartSyncLoaded = true;

  let isSyncing = false;
  let syncTimer = null;

  function getCartUrl() {
    return window.themeVariables?.routes?.cartUrl || "/cart";
  }

  function getCartAddUrl() {
    return (window.themeVariables?.routes?.cartAddUrl || "/cart/add") + ".js";
  }

  function getCartUpdateUrl() {
    return getCartUrl() + "/update.js";
  }

  function getCartJsonUrl() {
    return getCartUrl() + ".js";
  }

  function getProperty(item, key) {
    if (!item || !item.properties) return null;
    return Object.prototype.hasOwnProperty.call(item.properties, key) ? item.properties[key] : null;
  }

  function isTrue(value) {
    return value === true || String(value || "").toLowerCase() === "true";
  }

  function toNumber(value, fallback) {
    const number = parseInt(value, 10);
    return Number.isFinite(number) ? number : fallback;
  }

  function makeRuleKey(parentProductId, giftVariantId) {
    return String(parentProductId) + "::" + String(giftVariantId);
  }

  function isFreeGiftLine(item) {
    return isTrue(getProperty(item, "_free_gift"));
  }

  function getFreeGiftLineKey(item) {
    const parentProductId = getProperty(item, "_free_gift_parent_product_id");
    const giftVariantId = getProperty(item, "_free_gift_variant_id") || item.variant_id;

    if (!parentProductId || !giftVariantId) return null;

    return makeRuleKey(parentProductId, giftVariantId);
  }

  function buildGiftGroups(cart) {
    const groups = {};

    if (!cart || !Array.isArray(cart.items)) return groups;

    cart.items.forEach((item) => {
      if (isFreeGiftLine(item)) return;

      const giftVariantId = getProperty(item, "_pdp_free_gift_variant_id");
      if (!giftVariantId) return;

      const parentProductId = getProperty(item, "_pdp_free_gift_parent_product_id") || item.product_id;
      const maxQty = Math.max(toNumber(getProperty(item, "_pdp_free_gift_max_qty"), 3), 1);
      const key = makeRuleKey(parentProductId, giftVariantId);

      if (!groups[key]) {
        groups[key] = {
          key: key,
          parentProductId: parentProductId,
          giftVariantId: giftVariantId,
          paidQty: 0,
          maxQty: maxQty,
          desiredGiftQty: 0
        };
      }

      groups[key].paidQty += toNumber(item.quantity, 0);
      groups[key].maxQty = Math.max(groups[key].maxQty, maxQty);
    });

    Object.keys(groups).forEach((key) => {
      const group = groups[key];
      group.desiredGiftQty = Math.min(Math.max(group.paidQty - 1, 0), group.maxQty);
    });

    return groups;
  }

  async function fetchCart() {
    const response = await fetch(getCartJsonUrl(), {
      credentials: "same-origin",
      headers: {
        "X-Requested-With": "XMLHttpRequest"
      }
    });

    return response.json();
  }

  async function syncFreeGiftCart(cartFromEvent) {
    if (isSyncing) return;

    isSyncing = true;

    try {
      const cart = cartFromEvent && Array.isArray(cartFromEvent.items)
        ? cartFromEvent
        : await fetchCart();

      const groups = buildGiftGroups(cart);
      const updates = {};
      const handledGiftGroups = {};
      const addItems = [];

      if (!cart || !Array.isArray(cart.items)) return;

      cart.items.forEach((item) => {
        if (!isFreeGiftLine(item)) return;

        const giftGroupKey = getFreeGiftLineKey(item);

        if (!giftGroupKey || !groups[giftGroupKey]) {
          updates[item.key] = 0;
          return;
        }

        const desiredQty = groups[giftGroupKey].desiredGiftQty;

        if (handledGiftGroups[giftGroupKey]) {
          updates[item.key] = 0;
          return;
        }

        handledGiftGroups[giftGroupKey] = true;

        if (toNumber(item.quantity, 0) !== desiredQty) {
          updates[item.key] = desiredQty;
        }
      });

      Object.keys(groups).forEach((key) => {
        const group = groups[key];

        if (group.desiredGiftQty <= 0) return;
        if (handledGiftGroups[key]) return;

        addItems.push({
          id: Number(group.giftVariantId),
          quantity: group.desiredGiftQty,
          properties: {
            _free_gift: "true",
            _free_gift_parent_product_id: String(group.parentProductId),
            _free_gift_variant_id: String(group.giftVariantId),
            _free_gift_max_qty: String(group.maxQty)
          }
        });
      });

      const hasUpdates = Object.keys(updates).length > 0;
      const hasAdds = addItems.length > 0;

      if (!hasUpdates && !hasAdds) return;

      if (hasUpdates) {
        await fetch(getCartUpdateUrl(), {
          body: JSON.stringify({ updates: updates }),
          credentials: "same-origin",
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest"
          }
        });
      }

      if (hasAdds) {
        await fetch(getCartAddUrl(), {
          body: JSON.stringify({ items: addItems }),
          credentials: "same-origin",
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest"
          }
        });
      }

      const finalCart = await fetchCart();

      if (window.themeVariables?.settings?.pageType === "cart") {
        window.location.reload();
        return;
      }

      document.documentElement.dispatchEvent(new CustomEvent("cart:updated", {
        bubbles: true,
        detail: {
          cart: finalCart
        }
      }));

      document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
        bubbles: true,
        detail: {
          cart: finalCart,
          replacementDelay: 0
        }
      }));
    } catch (error) {
      console.error("PDP free gift sync failed:", error);
    } finally {
      setTimeout(function () {
        isSyncing = false;
      }, 0);
    }
  }

  function scheduleFreeGiftSync(cart) {
    if (isSyncing) return;

    clearTimeout(syncTimer);

    syncTimer = setTimeout(function () {
      syncFreeGiftCart(cart);
    }, 250);
  }

  document.documentElement.addEventListener("cart:updated", function (event) {
    scheduleFreeGiftSync(event.detail?.cart);
  });

  document.addEventListener("DOMContentLoaded", function () {
    if (
      window.themeVariables?.settings?.pageType === "cart" ||
      document.querySelector("cart-drawer")
    ) {
      scheduleFreeGiftSync();
    }
  });
})();